'use client';

import Image from 'next/image';
import Link from 'next/link';
import { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@awesome.me/kit-94e593bf23/icons/sharp/light';
import { faLockKeyhole } from '@awesome.me/kit-94e593bf23/icons/classic/solid';
import { faSpinnerThird } from '@awesome.me/kit-94e593bf23/icons/classic/light';
import { faCheck } from '@awesome.me/kit-94e593bf23/icons/sharp/solid';
import { faHeart } from '@awesome.me/kit-94e593bf23/icons/classic/solid';
import { signIn, useSession } from 'next-auth/react';
import { useGlobalMessage } from '@/app/_lib/hooks';
import { useReCaptcha } from 'next-recaptcha-v3';
import { trackLogin } from '@/app/_lib/analytics/ga4';
import removeSwatchFavoriteAction from '@/app/_lib/actions/RemoveSwatchFavoriteAction';
import orderSwatchSamplesAction from '@/app/_lib/actions/OrderSwatchSamplesAction';
import getPrimaryShippingAddressAction from '@/app/_lib/actions/GetPrimaryShippingAddressAction';
import checkSwatchSampleCooldownAction from '@/app/_lib/actions/CheckSwatchSampleCooldownAction';
import FormError from '@/app/_components/FormError';
import RecaptchaDisclaimer from '@/app/_components/RecaptchaDisclaimer';
const MAX_SAMPLE_ORDER = 5;
const PURCHASE_TIME_OPTIONS = [{
  value: '',
  label: ''
}, {
  value: 'within_1_week',
  label: 'Within 1 week'
}, {
  value: 'within_1_month',
  label: 'Within 1 month'
}, {
  value: 'within_3_months',
  label: 'Within 3 months'
}, {
  value: 'within_6_months',
  label: 'Within 6 months'
}, {
  value: 'just_browsing',
  label: 'Just browsing'
}];
const SwatchFavorites = ({
  activeTab,
  favorites,
  setFavorites,
  favoritesCount,
  setFavoritesCount,
  subProductId,
  onClose
}) => {
  const {
    data: session
  } = useSession();
  const showToast = useGlobalMessage();
  const {
    executeRecaptcha
  } = useReCaptcha();
  const [selectedSwatches, setSelectedSwatches] = useState({});
  const [isRemoving, setIsRemoving] = useState(null);
  const [isPending, setIsPending] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [serverResponse, setServerResponse] = useState({
    message: false,
    errors: {}
  });
  const [orderError, setOrderError] = useState(null);
  const [orderSuccess, setOrderSuccess] = useState(false);
  const [cooldownActive, setCooldownActive] = useState(false);
  const [cooldownMessage, setCooldownMessage] = useState('');

  // Form fields - pre-populated from customer data
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    companyName: '',
    companyWebsite: '',
    address1: '',
    address2: '',
    city: '',
    state: '',
    zip: '',
    phone: '',
    purchaseTime: '',
    notes: ''
  });

  // Pre-populate form with primary shipping address when customer is logged in
  useEffect(() => {
    const fetchShippingAddress = async () => {
      if (session?.user?.customerCode) {
        const response = await getPrimaryShippingAddressAction();
        if (response.success && response.data) {
          const addr = response.data;
          setFormData(prev => ({
            ...prev,
            fullName: [addr.shipping_first_name, addr.shipping_last_name].filter(Boolean).join(' ') || '',
            address1: addr.shipping_street_1 || '',
            address2: addr.shipping_street_2 || '',
            city: addr.shipping_city || '',
            state: addr.shipping_state || '',
            zip: addr.shipping_zip || '',
            email: session.user.email || '',
            phone: addr.shipping_phone || ''
          }));
        } else {
          // Fallback to email only if no shipping address
          setFormData(prev_0 => ({
            ...prev_0,
            email: session.user.email || ''
          }));
        }
      }
    };
    fetchShippingAddress();
  }, [session]);

  // Check cooldown status when logged in
  useEffect(() => {
    const checkCooldown = async () => {
      if (session?.user?.customerCode) {
        const response_0 = await checkSwatchSampleCooldownAction();
        if (response_0.success && response_0.data) {
          if (!response_0.data.canOrder && response_0.data.reason === 'cooldown') {
            setCooldownActive(true);
            setCooldownMessage(response_0.data.message);
          }
        }
      }
    };
    checkCooldown();
  }, [session]);

  // Convert favorites object to sorted array (newest first)
  const favoritesArray = Object.values(favorites || {}).sort((a, b) => (b.order || 0) - (a.order || 0));
  const handleRemoveFavorite = async swatch_id => {
    setIsRemoving(swatch_id);
    const response_1 = await removeSwatchFavoriteAction(swatch_id);
    if (response_1.success) {
      setFavorites(response_1.data.favorites);
      setFavoritesCount(response_1.data.count);
      setSelectedSwatches(prev_1 => {
        const updated = {
          ...prev_1
        };
        delete updated[swatch_id];
        return updated;
      });
    } else {
      showToast({
        message: response_1.message || 'Error removing swatch',
        messageType: 'error'
      });
    }
    setIsRemoving(null);
  };
  const handleToggleSelect = swatch => {
    setSelectedSwatches(prev_2 => {
      const updated_0 = {
        ...prev_2
      };
      if (updated_0[swatch.swatch_id]) {
        delete updated_0[swatch.swatch_id];
      } else {
        if (Object.keys(prev_2).length >= MAX_SAMPLE_ORDER) {
          showToast({
            message: `Maximum of ${MAX_SAMPLE_ORDER} samples allowed per order`,
            messageType: 'warning'
          });
          return prev_2;
        }
        updated_0[swatch.swatch_id] = swatch;
      }
      return updated_0;
    });
  };
  const handleInputChange = e => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev_3 => ({
      ...prev_3,
      [name]: value
    }));
  };
  const handleSignInSubmit = async e_0 => {
    e_0.preventDefault();
    setIsPending(true);
    setServerResponse({
      message: false,
      errors: {}
    });
    const formElement = e_0.target;
    const data = new FormData(formElement);
    const recaptchaToken = await executeRecaptcha('login');
    if (!recaptchaToken) {
      setServerResponse({
        success: false,
        message: 'Security verification unavailable. Please refresh and try again.',
        errors: {}
      });
      setIsPending(false);
      return;
    }

    // Wrap in timeout to prevent infinite hang
    try {
      const signInPromise = signIn('credentials', {
        email: data.get('account_id'),
        password: data.get('password'),
        recaptchaToken: recaptchaToken,
        redirect: false
      });
      const timeoutPromise = new Promise((_, reject) => setTimeout(() => reject(new Error('SIGNIN_TIMEOUT')), 30000));
      const result = await Promise.race([signInPromise, timeoutPromise]);
      if (result?.ok) {
        trackLogin();
        showToast({
          message: 'Signed in successfully',
          messageType: 'success'
        });
        // Reload to get fresh session data
        window.location.reload();
      } else {
        setServerResponse({
          success: false,
          message: 'Invalid email or password. Please try again.',
          errors: {}
        });
        setIsPending(false);
      }
    } catch (error) {
      setServerResponse({
        success: false,
        message: error.message === 'SIGNIN_TIMEOUT' ? 'Sign in is taking too long. Please refresh and try again.' : 'An error occurred. Please try again.',
        errors: {}
      });
      setIsPending(false);
    }
  };
  const handleSubmitOrder = async e_1 => {
    e_1.preventDefault();
    if (Object.keys(selectedSwatches).length === 0) {
      setOrderError('Please select at least one swatch to order.');
      return;
    }
    if (!formData.purchaseTime) {
      setOrderError('Please select when you plan to purchase.');
      return;
    }
    setIsSubmitting(true);
    setOrderError(null);

    // Convert purchase time value to human-readable label
    const purchaseTimeLabel = PURCHASE_TIME_OPTIONS.find(opt => opt.value === formData.purchaseTime)?.label || formData.purchaseTime;
    const response_2 = await orderSwatchSamplesAction({
      swatches: Object.values(selectedSwatches),
      fullName: formData.fullName.trim(),
      email: formData.email.trim(),
      companyName: formData.companyName.trim() || null,
      companyWebsite: formData.companyWebsite.trim() || null,
      address1: formData.address1.trim(),
      address2: formData.address2.trim() || null,
      city: formData.city.trim(),
      state: formData.state.trim(),
      zip: formData.zip.trim(),
      phone: formData.phone.trim() || null,
      purchaseTime: purchaseTimeLabel,
      notes: formData.notes.trim() || null,
      subProductId: subProductId || null
    });
    setIsSubmitting(false);
    if (response_2.success) {
      showToast({
        message: response_2.message,
        messageType: 'success'
      });
      setOrderSuccess(true);
      setSelectedSwatches({});
      setFormData(prev_4 => ({
        ...prev_4,
        purchaseTime: '',
        notes: ''
      }));
    } else {
      setOrderError(response_2.message || 'An error occurred. Please try again.');
    }
  };
  const selectedCount = Object.keys(selectedSwatches).length;
  const isLoggedIn = !!session?.user?.customerCode;

  // Empty state
  if (favoritesArray.length === 0) {
    return <div className={`w-full px-[var(--site-padding)] py-4 ${activeTab === 'favorites' ? 'block' : 'hidden'}`}>
                <div className="text-center py-8 text-light">
                    <p className="mb-2">No swatch favorites yet.</p>
                    <p className="text-xs">Click the heart icon on a swatch to add it to your favorites.</p>
                </div>
            </div>;
  }

  // Success state
  if (orderSuccess) {
    return <div className={`w-full px-[var(--site-padding)] py-4 ${activeTab === 'favorites' ? 'block' : 'hidden'}`}>
                <div className="text-center py-8">
                    <h3 className="text-lg font-medium mb-2">Thank you. Samples Request Received.</h3>
                    <p className="text-sm text-light mb-6">
                        We will be in touch shortly.
                    </p>
                    <button onClick={onClose} className="btn btn-secondary">
                        Close
                    </button>
                </div>
            </div>;
  }
  return <div className={`w-full px-[var(--site-padding)] py-4 ${activeTab === 'favorites' ? 'block' : 'hidden'}`}>
            <div className="flex flex-col md:flex-row gap-6">
                {/* Left column - Favorites list */}
                <div className="md:w-1/2">
                    <h3 className="text-lg mb-1">
                        My Favorites <span className="text-light-2">({favoritesCount})</span>
                    </h3>
                    <p className="text-xs text-light mb-4">
                        Select swatches you would like samples of (limit {MAX_SAMPLE_ORDER})
                    </p>

                    <div className="space-y-3">
                        {favoritesArray.map(swatch_0 => {
            const samplesAvailable = swatch_0.no_samples != 1;
            const isSelected = !!selectedSwatches[swatch_0.swatch_id];
            return <div key={swatch_0.swatch_id} className={`flex items-start gap-3 p-2 relative ${samplesAvailable ? 'cursor-pointer' : 'cursor-default'}`} onClick={() => samplesAvailable && handleToggleSelect(swatch_0)}>
                                    {/* Toggle switch */}
                                    {samplesAvailable ? <div className="shrink-0 flex items-center h-[60px]">
                                            <div className={`w-8 h-4 rounded-full transition-colors relative ${isSelected ? 'bg-primary' : 'bg-gray-300'}`}>
                                                <div className={`absolute top-0.5 w-3 h-3 rounded-full bg-white shadow transition-all ${isSelected ? 'left-[18px]' : 'left-0.5'}`} />
                                            </div>
                                        </div> : <div className="shrink-0 w-8" />}

                                    {/* Swatch image with name to the right */}
                                    <div className="flex-1 min-w-0 flex items-start gap-3">
                                        <div className="relative shrink-0">
                                            <button onClick={e_2 => {
                    e_2.stopPropagation();
                    handleRemoveFavorite(swatch_0.swatch_id);
                  }} disabled={isRemoving === swatch_0.swatch_id} className="absolute top-0 right-1.5 z-10 text-red hover:text-red/70 transition-colors" aria-label="Remove from favorites">
                                                {isRemoving === swatch_0.swatch_id ? <FontAwesomeIcon icon={faSpinnerThird} className="text-xs animate-spin" /> : <FontAwesomeIcon icon={faHeart} className="text-xs" />}
                                            </button>
                                            <Image src={`${process.env.NEXT_PUBLIC_RESOURCE_URL}/swatch_photos/${swatch_0.swatch_photo}`} alt={swatch_0.swatch_name} width={60} height={60} className="w-[60px] h-[60px] object-cover" />
                                        </div>
                                        <div className="min-w-0">
                                            <div className="text-2xs">
                                                <span className="font-medium">{swatch_0.swatch_name}</span>
                                                {swatch_0.swatch_code && <span className="text-light"> ({swatch_0.swatch_code})</span>}
                                            </div>
                                            {swatch_0.swatch_line_name && <div className="text-3xs text-light">{swatch_0.swatch_line_name}</div>}
                                            {!samplesAvailable && <div className="text-3xs text-red mt-0.5">
                                                    samples n/a
                                                </div>}
                                        </div>
                                    </div>
                                </div>;
          })}
                    </div>
                </div>

                {/* Right column - Order form or Sign-in */}
                <div className="md:w-1/2">
                    {isLoggedIn && cooldownActive ? <>
                            <h3 className="text-lg mb-1">Order Samples</h3>
                            <p className="text-sm text-light mt-4">
                                {cooldownMessage}
                            </p>
                        </> : isLoggedIn ? <>
                            <h3 className="text-lg mb-1">
                                Order Samples <span className="text-light-2">({selectedCount})</span>
                            </h3>
                            <p className="text-xs text-light mb-4">
                                Check the boxes for all swatches you would like to order. Please limit samples to <strong>{MAX_SAMPLE_ORDER} swatches</strong>.
                            </p>

                            {orderError && <FormError addClasses="font-medium mb-4">{orderError}</FormError>}

                            <form onSubmit={handleSubmitOrder}>
                                <div className="form-group mb-3">
                                    <input type="text" id="sample_fullName" name="fullName" value={formData.fullName} onChange={handleInputChange} className="form-input peer" placeholder=" " autoComplete="name" />
                                    <label htmlFor="sample_fullName" className="form-input-label peer-placeholder-shown:form-input-label-placeholder-shown">
                                        Full Name
                                    </label>
                                </div>

                                <div className="form-group mb-3">
                                    <input type="email" id="sample_email" name="email" value={formData.email} onChange={handleInputChange} className="form-input peer" placeholder=" " autoComplete="email" />
                                    <label htmlFor="sample_email" className="form-input-label peer-placeholder-shown:form-input-label-placeholder-shown">
                                        Email
                                    </label>
                                </div>

                                <div className="form-group mb-3">
                                    <input type="text" id="sample_companyName" name="companyName" value={formData.companyName} onChange={handleInputChange} className="form-input peer" placeholder=" " autoComplete="organization" />
                                    <label htmlFor="sample_companyName" className="form-input-label peer-placeholder-shown:form-input-label-placeholder-shown">
                                        Firm Name
                                    </label>
                                </div>

                                <div className="form-group mb-3">
                                    <input type="text" id="sample_companyWebsite" name="companyWebsite" value={formData.companyWebsite} onChange={handleInputChange} className="form-input peer" placeholder=" " autoComplete="url" />
                                    <label htmlFor="sample_companyWebsite" className="form-input-label peer-placeholder-shown:form-input-label-placeholder-shown">
                                        Company Website
                                    </label>
                                </div>

                                <div className="form-group mb-3">
                                    <input type="text" id="sample_address1" name="address1" value={formData.address1} onChange={handleInputChange} className="form-input peer" placeholder=" " autoComplete="address-line1" />
                                    <label htmlFor="sample_address1" className="form-input-label peer-placeholder-shown:form-input-label-placeholder-shown">
                                        Address
                                    </label>
                                </div>

                                <div className="form-group mb-3">
                                    <input type="text" id="sample_address2" name="address2" value={formData.address2} onChange={handleInputChange} className="form-input peer" placeholder=" " autoComplete="address-line2" />
                                    <label htmlFor="sample_address2" className="form-input-label peer-placeholder-shown:form-input-label-placeholder-shown">
                                        Address 2
                                    </label>
                                </div>

                                <div className="form-group mb-3">
                                    <input type="text" id="sample_city" name="city" value={formData.city} onChange={handleInputChange} className="form-input peer" placeholder=" " autoComplete="address-level2" />
                                    <label htmlFor="sample_city" className="form-input-label peer-placeholder-shown:form-input-label-placeholder-shown">
                                        City
                                    </label>
                                </div>

                                <div className="form-group mb-3">
                                    <input type="text" id="sample_state" name="state" value={formData.state} onChange={handleInputChange} className="form-input peer" placeholder=" " autoComplete="address-level1" />
                                    <label htmlFor="sample_state" className="form-input-label peer-placeholder-shown:form-input-label-placeholder-shown">
                                        State
                                    </label>
                                </div>

                                <div className="form-group mb-3">
                                    <input type="text" id="sample_zip" name="zip" value={formData.zip} onChange={handleInputChange} className="form-input peer" placeholder=" " autoComplete="postal-code" />
                                    <label htmlFor="sample_zip" className="form-input-label peer-placeholder-shown:form-input-label-placeholder-shown">
                                        Zip
                                    </label>
                                </div>

                                <div className="form-group mb-3">
                                    <input type="tel" id="sample_phone" name="phone" value={formData.phone} onChange={handleInputChange} className="form-input peer" placeholder=" " autoComplete="tel" />
                                    <label htmlFor="sample_phone" className="form-input-label peer-placeholder-shown:form-input-label-placeholder-shown">
                                        Phone
                                    </label>
                                </div>

                                <div className="form-group mb-3">
                                    <select id="sample_purchaseTime" name="purchaseTime" value={formData.purchaseTime} onChange={handleInputChange} className="form-input peer" required>
                                        {PURCHASE_TIME_OPTIONS.map(option => <option key={option.value} value={option.value}>
                                                {option.label}
                                            </option>)}
                                    </select>
                                    <label htmlFor="sample_purchaseTime" className={`form-input-label ${!formData.purchaseTime ? 'form-input-label-placeholder-shown' : ''}`}>
                                        When do you plan on purchasing?
                                    </label>
                                </div>

                                <div className="form-group mb-4">
                                    <textarea id="sample_notes" name="notes" value={formData.notes} onChange={handleInputChange} className="form-input peer" placeholder=" " rows="2" />
                                    <label htmlFor="sample_notes" className="form-input-label peer-placeholder-shown:form-input-label-placeholder-shown">
                                        Notes
                                    </label>
                                </div>

                                <div className="flex items-center gap-4">
                                    <button type="submit" disabled={isSubmitting || selectedCount === 0} className={`btn btn-sm btn-primary px-8 ${isSubmitting || selectedCount === 0 ? 'opacity-50 cursor-not-allowed' : ''}`}>
                                        {isSubmitting ? <>
                                                <FontAwesomeIcon icon={faSpinnerThird} className="animate-spin mr-2" />
                                                Submitting...
                                            </> : 'Submit'}
                                    </button>
                                    <button type="button" onClick={() => setSelectedSwatches({})} className="text-sm text-light hover:text-dark">
                                        Cancel
                                    </button>
                                </div>
                            </form>
                        </> : <>
                            <h3 className="text-lg mb-1">
                                Order Samples <span className="text-light-2">({selectedCount})</span>
                            </h3>
                            <p className="text-xs text-light mb-4">
                                Please sign in to order free samples.
                            </p>

                            <form onSubmit={handleSignInSubmit}>
                                {serverResponse?.message && <FormError addClasses="font-medium mb-4">{serverResponse.message}</FormError>}

                                <div className="form-group mb-3">
                                    <input id="swatch_account_id" type="email" name="account_id" autoComplete="username" className="form-input peer" placeholder=" " required />
                                    <label htmlFor="swatch_account_id" className="form-input-label peer-placeholder-shown:form-input-label-placeholder-shown">
                                        Email <span className="text-primary">*</span>
                                    </label>
                                </div>

                                <div className="form-group mb-3">
                                    <input id="swatch_password" type="password" name="password" autoComplete="current-password" className="form-input peer" placeholder=" " required />
                                    <label htmlFor="swatch_password" className="form-input-label peer-placeholder-shown:form-input-label-placeholder-shown">
                                        Password <span className="text-primary">*</span>
                                    </label>
                                </div>

                                <Link className="text-2xs underline underline-offset-2 mb-4 inline-block" href="/account/lost_pass">
                                    Reset Password
                                </Link>

                                <button type="submit" disabled={isPending} className={`btn btn-primary w-full relative ${isPending ? 'opacity-75' : ''}`}>
                                    <FontAwesomeIcon icon={faLockKeyhole} className="absolute top-1/3 left-3 text-3xs" />
                                    {isPending ? 'Signing In...' : 'Sign In'}
                                </button>

                                <div className="mt-4 text-xs">
                                    Don&apos;t have an account?{' '}
                                    <Link href="/account/create" className="underline underline-offset-2">
                                        Create Account
                                    </Link>
                                </div>

                                <div className="mt-4">
                                    <RecaptchaDisclaimer />
                                </div>
                            </form>
                        </>}
                </div>
            </div>
        </div>;
};
export default SwatchFavorites;